import { type ReactNode } from "react";
import { ChartCard } from "../../components/observability/index.ts";
import type { ChartData, ChartSeries } from "../../components/index.ts";
import type { IdentityMcpDenialPage } from "./use-identity-mcp.ts";
import { reasonLabel } from "./vocab.ts";

/**
 * Denials by reason, stacked over time (task 7.3; requirement 13.3 "denials by reason stacked over
 * time").
 *
 * Each denial row carries a per-step `series` of counts for one `identity_mcp_reason`, all rows on
 * the SAME bucket grid (the server bucketed them by the operation's `step`). To read "which reasons
 * are driving denials over time" the rows are drawn as a STACKED area: for each bucket, every
 * reason's band sits on top of the ones below it, so the top edge is the total denials in that
 * bucket and each band's thickness is that reason's share. The stacking is computed here by running
 * a cumulative sum across the reasons (bottom reason = its own values; each higher reason = itself
 * plus every reason below), which the line-area Chart wrapper renders as filled bands — the data
 * table alternative the wrapper always ships still shows each reason's cumulative value honestly.
 *
 * Only reasons with at least one non-zero bucket are plotted, so the zero-filled catalog members
 * that never fired do not clutter the chart (they are still visible, zero, in the denials table).
 * The busiest reasons are drawn first (bottom of the stack) so the dominant bands read clearly. An
 * empty window shows the chart's honest "no denials in range" state rather than a flat zero line.
 */

type DenialRow = IdentityMcpDenialPage["items"][number];

/** A stable palette for the stacked reason bands; theme-neutral hues that read in dark and light. */
const REASON_PALETTE = [
  "#e86a5c",
  "#f0ab3d",
  "#8f6bd4",
  "#4aa3ff",
  "#2bb5a0",
  "#d4719e",
  "#5f8aa5",
  "#c0a35e",
  "#7aa7c4",
  "#9d6b4f",
] as const;

export function DenialsChart(props: { items: readonly DenialRow[] }): ReactNode {
  // Keep only reasons that actually fired, busiest first (bottom of the stack reads clearest).
  const active = props.items
    .filter((row) => row.series.some((p) => (p.count.value ?? 0) > 0))
    .sort((a, b) => rowTotal(b) - rowTotal(a));

  // Build the shared x grid from the first active row (every row shares the same bucket grid). An
  // empty active set leaves the x grid empty, which the chart renders as its honest empty state.
  const xs = (active[0]?.series ?? []).map((p) => p.t);

  // The whole chart's confidence rides on the series exactness: estimated when sampling weighted the
  // counts (design 12.3). If any plotted point was estimated, the bands are labelled "est.".
  const estimated = active.some((row) => row.series.some((p) => p.count.exactness === "estimated"));
  const measure = estimated ? "estimate" : "exact";

  // Cumulative stack: band[i] at bucket j = sum of reasons[0..i] raw values at j. Drawn with a fill
  // so each band's thickness is that reason's share and the top edge is the per-bucket total.
  let running = xs.map(() => 0);
  const bands: number[][] = [];
  for (const row of active) {
    const raw = row.series.map((p) => p.count.value ?? 0);
    running = running.map((acc, j) => acc + (raw[j] ?? 0));
    bands.push([...running]);
  }

  const data: ChartData = [xs, ...bands];
  const chartSeries: readonly ChartSeries[] = active.map((row, i) => {
    const color = REASON_PALETTE[i % REASON_PALETTE.length] as string;
    return {
      label: reasonLabel(row.reason),
      colorVar: color,
      fillVar: color,
      width: 1,
      measure,
    };
  });

  const footer = (
    <span className="imcp-section__note">
      Stacked by reason; the top edge is total denials per bucket
      {measure === "estimate" ? " · sampled (est.)" : ""}.
    </span>
  );

  return (
    <ChartCard
      title="Denials by reason over time"
      data={data}
      series={chartSeries}
      height={260}
      empty="No Identity MCP denials were recorded in this window. Widen the time range, or check that an Identity MCP source is reporting."
      footer={footer}
    />
  );
}

/** The numeric total for a denial row across all buckets (busiest-first ordering). */
function rowTotal(row: DenialRow): number {
  const t = row.total.value;
  if (t !== null) return t;
  return row.series.reduce((sum, p) => sum + (p.count.value ?? 0), 0);
}
